import React from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Head from 'next/head';

import { ClientApiService } from 'services/ClientApiService';
import { BookTime as BookTimeView } from 'components/views/BookTime';
import { StrapiService } from 'lib/strapi/StrapiService';
import { MagazineArticleCard } from 'components/sections/Magazine';
import { withSessionSsr } from 'lib/auth';

interface FormData {
  licensePlate: string;
  mileage: string;
}

const validationSchema = yup
  .object({
    licensePlate: yup
      .string()
      .matches(/^[A-Za-z]{3}\d{2}[A-Za-z\d]$/, 'Vänligen fyll i ett korrekt registreringsnummer')
      .label('Registreringsnummer'),
  })
  .required();

interface Props {
  articles: MagazineArticleCard[];
}

const BookTime: NextPage<Props> = ({ articles }) => {
  const router = useRouter();
  const canonicalUrl = `https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}${router.asPath}`;

  const allowedRejectReasons = ['older_than_years_limit', 'travelled_more_than_limit'];
  const methods = useForm<FormData>({ resolver: yupResolver(validationSchema) });

  const onSubmit = async (data: FormData) => {
    // console.log(data);
    // call car check
    const response = await ClientApiService.checkCar(data.licensePlate, data.mileage, 'buyer');
    const responseJson = await response.json();

    if (
      response.status !== 200 ||
      responseJson.response.errors?.length > 0 ||
      responseJson.response.status_code > 200
    ) {
      // console.log(responseJson);
      methods.setError('licensePlate', {
        type: 'manual',
        message:
          responseJson.response.errors[0] === 'Could not find a car with this license plate.'
            ? 'Vi kunde inte hitta en bil med det registreringsnumret'
            : responseJson.response.errors[0],
      });
      return;
    }

    if (responseJson.response.payload?.status === 'APPROVAL_REJECTED') {
      const reasonCodes = Object.entries(responseJson.response.payload.rejection_reasons || {}).map(
        ([key]) => key
      );
      let onlyAllowedReason = true;
      reasonCodes.forEach((reason) => {
        if (!allowedRejectReasons.includes(reason)) {
          onlyAllowedReason = false;
          methods.setError('licensePlate', {
            type: 'manual',
            message:
              'Den här bilen kan du inte köpa med Blipp. Kontakta kundtjänst på hej@blipp.se om du har frågor.',
          });
        }
      });
      if (onlyAllowedReason) {
        router.push(`/app/buy/${data.licensePlate}/book?price=0`);
      }
      // rejected
      // console.log(responseJson);

      return;
    }

    if (responseJson.response.payload?.status === 'APPROVAL_FAILED') {
      // trials
      methods.setError('licensePlate', {
        type: 'manual',
        message:
          'Den här bilen behöver vi titta närmare på. Kontakta kundtjänst på hej@blipp.se om du har frågor.',
      });
      return;
    }

    // Push to calander
    await router.push(`/app/buy/${data.licensePlate}/book?price=0`);
  };

  return (
    <FormProvider {...methods}>
      <Head>
        <title>Boka tid med Blipp - Experthjälp på garageuppfarten</title>
        <meta property="og:title" content="Boka tid med Blipp - Experthjälp på garageuppfarten" />
        <meta
          property="og:description"
          content="När du har hittat bilen du vill köpa och kommit överens med säljaren om tid och plats, bokar du tid med Blipp för en trygg och smidig affär."
        />
        <meta
          name="description"
          content="När du har hittat bilen du vill köpa och kommit överens med säljaren om tid och plats, bokar du tid med Blipp för en trygg och smidig affär."
        />
        <link rel="canonical" key="canonical" href={canonicalUrl} />
      </Head>

      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <BookTimeView articles={articles} />
      </form>
    </FormProvider>
  );
};

export const getServerSideProps: GetServerSideProps = withSessionSsr(async () => {
  const articles = await StrapiService.getLatestMagazineArticles(3);

  return {
    props: {
      articles,
    },
  };
});

export default BookTime;
